import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '@/lib/api';
import { quoteServiceOptions } from '@/data/services';
import { company } from '@/data/company';
import { Loader, Check, Phone, ArrowRight, Mail, MapPin, Briefcase, Message, WhatsApp } from '@/components/ui/Icons';
import { Captcha, type CaptchaState } from '@/components/forms/Captcha';

const schema = z.object({
  name: z.string().min(2, 'Please enter your name').max(120),
  email: z.string().email('Please enter a valid email').max(190),
  phone: z.string().max(40).optional().or(z.literal('')),
  address: z.string().max(255).optional().or(z.literal('')),
  service: z.string().min(1, 'Please choose a service'),
  message: z.string().min(10, 'Please tell us a little about the job').max(4000),
  // honeypot
  company_website: z.string().max(0, '').optional().or(z.literal('')),
});

type FormData = z.infer<typeof schema>;

export function QuoteForm({ defaultService }: { defaultService?: string }) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { service: defaultService ?? '' },
    mode: 'onTouched',
  });

  const [captcha, setCaptcha] = useState<CaptchaState | null>(null);
  const [captchaError, setCaptchaError] = useState<string | undefined>(undefined);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const message = watch('message') ?? '';

  const onSubmit = async (values: FormData) => {
    setServerError(null);
    setCaptchaError(undefined);
    if (!captcha || !captcha.answer.trim()) {
      setCaptchaError('Please answer the quick check to send your enquiry.');
      return;
    }
    try {
      await api('/quotes', {
        method: 'POST',
        body: JSON.stringify({
          ...values,
          captcha_id: captcha.id,
          captcha_answer: captcha.answer,
        }),
      });
      setSubmitted(true);
      reset({ service: defaultService ?? '' });
      setCaptcha(null);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'error' in err
          ? String((err as { error: unknown }).error)
          : 'Something went wrong. Please try again or call us directly.';
      // Captcha-related errors come back as 422s with this hint.
      if (/answer|verification|check/i.test(message)) {
        setCaptchaError(message);
        setCaptcha(null); // force a fresh challenge
      } else {
        setServerError(message);
      }
    }
  };

  if (submitted) {
    return (
      <div className="rounded-2xl border border-gold-200 bg-paper-50 p-6 text-center sm:p-8">
        <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-gold-400/20 text-gold-700">
          <Check className="h-6 w-6" />
        </span>
        <h3 className="font-display text-2xl font-semibold text-navy-900">
          Thanks, we’ve got it.
        </h3>
        <p className="mx-auto mt-2 max-w-md text-ink-700">
          Your enquiry is on the way to our team. We’ll be in touch shortly. Need it sooner?
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <a href={`tel:${company.phoneTel}`} className="btn-gold">
            <Phone className="h-4 w-4" /> {company.phone}
          </a>
          <a
            href={`https://wa.me/${company.whatsappTel}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline-gold"
          >
            <WhatsApp className="h-4 w-4" /> WhatsApp us
          </a>
        </div>
        <button
          type="button"
          className="btn-ghost mt-5 text-sm"
          onClick={() => setSubmitted(false)}
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="space-y-5 rounded-2xl border border-paper-100 bg-paper-0 p-6 shadow-card sm:p-8"
    >
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-700">
          Free, no-obligation quote
        </p>
        <h3 className="mt-1 font-display text-2xl font-semibold text-navy-900">
          Tell us about your project
        </h3>
        <p className="mt-1 text-sm text-ink-700">
          Reply usually within one working day. For 24/7 emergencies, call{' '}
          <a href={`tel:${company.phoneTel}`} className="font-semibold text-navy-800 underline-offset-2 hover:underline">
            {company.phone}
          </a>
          .
        </p>
      </header>

      {/* Section 1: contact */}
      <fieldset className="space-y-4">
        <legend className="text-xs font-semibold uppercase tracking-wider text-ink-700/70">
          Your details
        </legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" error={errors.name?.message} required Icon={Briefcase}>
            <input
              type="text"
              autoComplete="name"
              placeholder="Jane Smith"
              {...register('name')}
              className={inputCls}
            />
          </Field>
          <Field label="Email" error={errors.email?.message} required Icon={Mail}>
            <input
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              {...register('email')}
              className={inputCls}
            />
          </Field>
          <Field label="Phone" hint="optional but helps us reply quickly" error={errors.phone?.message} Icon={Phone}>
            <input
              type="tel"
              autoComplete="tel"
              placeholder="07000 000000"
              {...register('phone')}
              className={inputCls}
            />
          </Field>
          <Field label="Address or postcode" hint="optional" error={errors.address?.message} Icon={MapPin}>
            <input
              type="text"
              autoComplete="street-address"
              placeholder="CM7 1AA"
              {...register('address')}
              className={inputCls}
            />
          </Field>
        </div>
      </fieldset>

      {/* Section 2: project */}
      <fieldset className="space-y-4">
        <legend className="text-xs font-semibold uppercase tracking-wider text-ink-700/70">
          Your project
        </legend>
        <Field label="What can we help with?" error={errors.service?.message} required>
          <select {...register('service')} className={inputCls}>
            <option value="">Choose a service…</option>
            {quoteServiceOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </Field>

        <Field
          label="Tell us about the job"
          hint={`${message.length}/4000`}
          error={errors.message?.message}
          required
          Icon={Message}
        >
          <textarea
            rows={5}
            placeholder="Roughly what's the job, where is it, and when would you like it done?"
            maxLength={4000}
            {...register('message')}
            className={inputCls}
          />
        </Field>
      </fieldset>

      {/* honeypot - hidden from users + screen readers */}
      <div aria-hidden="true" className="hidden">
        <label>
          Company website
          <input type="text" tabIndex={-1} autoComplete="off" {...register('company_website')} />
        </label>
      </div>

      <Captcha value={captcha} onChange={setCaptcha} error={captchaError} />

      {serverError && (
        <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {serverError}
        </p>
      )}

      <button type="submit" className="btn-gold w-full text-base" disabled={isSubmitting}>
        {isSubmitting ? <Loader className="h-4 w-4" /> : null}
        {isSubmitting ? 'Sending…' : 'Send my quote request'}
        {!isSubmitting && <ArrowRight className="h-3.5 w-3.5" />}
      </button>

      <p className="text-center text-xs text-ink-700">
        By sending, you agree we can contact you about your enquiry. We don’t share your details.
      </p>
    </form>
  );
}

function Field({
  label,
  hint,
  error,
  required,
  children,
  Icon,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
  Icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-center justify-between text-sm font-semibold text-navy-800">
        <span className="inline-flex items-center gap-1.5">
          {Icon ? <Icon className="h-3 w-3 text-gold-600" /> : null}
          {label}
          {required && <span className="text-gold-600">*</span>}
        </span>
        {hint && <span className="text-xs font-normal text-ink-700/70">{hint}</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-sm text-red-700">{error}</span>}
    </label>
  );
}

const inputCls =
  'block w-full rounded-md border border-paper-100 bg-paper-0 px-3 py-2.5 text-ink-900 shadow-xs focus:border-gold-400 focus:outline-hidden focus:ring-2 focus:ring-gold-200';
